import React, { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Listbox, ListboxButton, ListboxOptions, ListboxOption } from '@headlessui/react';
import {
  TrendingDown,
  DollarSign,
  Package,
  BarChart3,
  ChevronDown,
  Check,
  Filter,
  Truck,
} from 'lucide-react';
import { ReportsService, type BottomProduct } from '../../services/reports/reports.service';
import { SuppliersService } from '../../services/suppliers/suppliers.service';
import { DatePickerInput } from '../ui/DatePickerInput';
import { getColombiaDate, toColombiaDateString } from '../../utils/date';

const formatMoney = (amount: number) => {
  return new Intl.NumberFormat('es-CO', {
    style: 'currency',
    currency: 'COP',
    minimumFractionDigits: 0,
  }).format(amount);
};

const KPICard: React.FC<{
  title: string;
  value: string;
  subtitle?: string;
  icon: React.ReactNode;
  iconBg: string;
  iconColor: string;
}> = ({ title, value, subtitle, icon, iconBg, iconColor }) => (
  <div className="bg-white p-5 rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
    <div className="flex items-start justify-between">
      <div>
        <p className="text-sm font-medium text-gray-500">{title}</p>
        <p className="text-2xl font-bold text-gray-900 mt-1">{value}</p>
        {subtitle && <p className="text-xs text-gray-400 mt-1">{subtitle}</p>}
      </div>
      <div className={`p-3 rounded-xl ${iconBg}`}>
        <div className={iconColor}>{icon}</div>
      </div>
    </div>
  </div>
);

const KPICardSkeleton = () => (
  <div className="bg-white p-5 rounded-xl border border-gray-200 shadow-sm animate-pulse">
    <div className="flex items-start justify-between">
      <div className="flex-1">
        <div className="h-4 bg-gray-200 rounded w-24 mb-2" />
        <div className="h-8 bg-gray-200 rounded w-32" />
      </div>
      <div className="p-3 rounded-xl bg-gray-200 w-12 h-12" />
    </div>
  </div>
);

export const ProductosMenosRotanContent: React.FC = () => {
  const [dateRange, setDateRange] = useState<'month' | 'quarter' | 'year' | 'custom'>('month');
  const [fechaInicio, setFechaInicio] = useState('');
  const [fechaFin, setFechaFin] = useState('');
  const [proveedorId, setProveedorId] = useState<string>('');

  const { data: proveedores } = useQuery({
    queryKey: ['proveedores-dropdown-rotacion'],
    queryFn: async () => {
      const r = await SuppliersService.getAllForSelect();
      return r.success ? r.data : [];
    },
    staleTime: Infinity,
  });

  const getDates = () => {
    const hoy = getColombiaDate();
    const hoyStr = toColombiaDateString(hoy);
    let inicioStr = hoyStr;

    switch (dateRange) {
      case 'month': {
        const inicio = new Date(hoy.getFullYear(), hoy.getMonth(), 1);
        inicioStr = toColombiaDateString(inicio);
        break;
      }
      case 'quarter': {
        const inicio = new Date(hoy);
        inicio.setDate(hoy.getDate() - 90);
        inicioStr = toColombiaDateString(inicio);
        break;
      }
      case 'year': {
        const inicio = new Date(hoy.getFullYear(), 0, 1);
        inicioStr = toColombiaDateString(inicio);
        break;
      }
      case 'custom':
        return {
          inicio: fechaInicio || hoyStr,
          fin: fechaFin || hoyStr,
        };
    }
    return { inicio: inicioStr, fin: hoyStr };
  };

  const filters = useMemo(() => {
    const { inicio, fin } = getDates();
    return {
      fechaInicio: inicio,
      fechaFin: fin,
      proveedorId: proveedorId || undefined,
    };
  }, [dateRange, fechaInicio, fechaFin, proveedorId]);

  const query = useQuery({
    queryKey: ['bottom-products', filters],
    queryFn: () => ReportsService.getBottomProducts(filters, 100),
    staleTime: 5 * 60 * 1000,
  });

  const productos = query.data?.success ? query.data.data : [];

  const kpis = useMemo(() => {
    const totalProductos = productos.length;
    const stockInmovilizado = productos.reduce((sum, p) => sum + p.stockBase, 0);
    const valorEstimado = productos.reduce((sum, p) => sum + p.totalVentas, 0);
    const sinVentas = productos.filter((p) => p.cantidadVendida === 0).length;
    return { totalProductos, stockInmovilizado, valorEstimado, sinVentas };
  }, [productos]);

  const isLoading = query.isLoading && !query.data;

  return (
    <div>
      {/* Filtros */}
      <div className="flex flex-wrap items-center gap-2 mb-6">
        <div className="flex rounded-lg border border-gray-200 overflow-hidden">
          {[
            { key: 'month', label: 'Este mes' },
            { key: 'quarter', label: 'Últimos 3 meses' },
            { key: 'year', label: 'Este año' },
            { key: 'custom', label: 'Personalizado' },
          ].map((opt) => (
            <button
              key={opt.key}
              onClick={() => setDateRange(opt.key as any)}
              className={`px-4 py-2 text-sm font-medium transition-colors ${
                dateRange === opt.key
                  ? 'bg-emerald-600 text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>

        <Listbox value={proveedorId} onChange={setProveedorId}>
          <div className="relative">
            <ListboxButton className="relative w-[220px] cursor-pointer rounded-lg border border-gray-200 bg-white py-2 pl-3 pr-10 text-left text-sm text-gray-900 hover:border-emerald-300 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all">
              <span className="block truncate">
                {proveedorId ? proveedores?.find((p) => p.id === proveedorId)?.nombre : 'Todos los proveedores'}
              </span>
              <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                <ChevronDown className="h-4 w-4 text-gray-400" aria-hidden="true" />
              </span>
            </ListboxButton>
            <ListboxOptions
              transition
              className="absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-lg bg-white py-1 text-sm shadow-lg ring-1 ring-black/5 focus:outline-none data-[closed]:scale-95 data-[closed]:opacity-0 data-[enter]:duration-100 data-[leave]:duration-75 data-[enter]:ease-out data-[leave]:ease-in"
            >
              <ListboxOption
                value=""
                className="group relative cursor-pointer select-none py-2 pl-3 pr-9 text-gray-900 data-[focus]:bg-emerald-50 data-[focus]:text-emerald-900 transition-colors"
              >
                <span className="block truncate">Todos los proveedores</span>
                <span className="absolute inset-y-0 right-0 flex items-center pr-3 text-emerald-600 opacity-0 group-data-[selected]:opacity-100">
                  <Check className="h-4 w-4" />
                </span>
              </ListboxOption>
              {proveedores?.map((p) => (
                <ListboxOption
                  key={p.id}
                  value={p.id}
                  className="group relative cursor-pointer select-none py-2 pl-3 pr-9 text-gray-900 data-[focus]:bg-emerald-50 data-[focus]:text-emerald-900 transition-colors"
                >
                  <span className="block truncate">{p.nombre}</span>
                  <span className="absolute inset-y-0 right-0 flex items-center pr-3 text-emerald-600 opacity-0 group-data-[selected]:opacity-100">
                    <Check className="h-4 w-4" />
                  </span>
                </ListboxOption>
              ))}
            </ListboxOptions>
          </div>
        </Listbox>

        {dateRange === 'custom' && (
          <div className="flex items-center gap-2">
            <DatePickerInput value={fechaInicio} onChange={setFechaInicio} placeholder="Fecha inicio" />
            <span className="text-gray-500">a</span>
            <DatePickerInput value={fechaFin} onChange={setFechaFin} placeholder="Fecha fin" />
          </div>
        )}

        <button
          onClick={() => query.refetch()}
          className="p-2 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 transition-colors"
          title="Recargar datos"
        >
          <Filter className="w-5 h-5" />
        </button>

        {proveedorId && (
          <button
            onClick={() => setProveedorId('')}
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-gray-200 bg-white text-sm text-gray-700 hover:bg-gray-50 transition-colors"
          >
            <Truck className="w-4 h-4 text-emerald-600" />
            <span className="truncate max-w-[150px]">
              {proveedores?.find((p) => p.id === proveedorId)?.nombre}
            </span>
            <span className="text-gray-400">×</span>
          </button>
        )}
      </div>

      {/* KPIs */}
      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <KPICardSkeleton />
          <KPICardSkeleton />
          <KPICardSkeleton />
          <KPICardSkeleton />
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <KPICard
            title="Productos con Baja Rotación"
            value={kpis.totalProductos.toString()}
            subtitle="En el período seleccionado"
            icon={<TrendingDown className="w-6 h-6" />}
            iconBg="bg-red-100"
            iconColor="text-red-600"
          />
          <KPICard
            title="Stock Inmovilizado"
            value={kpis.stockInmovilizado.toLocaleString('es-CO')}
            subtitle="Unidades en inventario"
            icon={<Package className="w-6 h-6" />}
            iconBg="bg-blue-100"
            iconColor="text-blue-600"
          />
          <KPICard
            title="Valor en Stock"
            value={formatMoney(kpis.valorEstimado)}
            subtitle="Ventas en el período"
            icon={<DollarSign className="w-6 h-6" />}
            iconBg="bg-amber-100"
            iconColor="text-amber-600"
          />
          <KPICard
            title="Sin Ventas"
            value={kpis.sinVentas.toString()}
            subtitle="Productos sin movimiento"
            icon={<BarChart3 className="w-6 h-6" />}
            iconBg="bg-gray-100"
            iconColor="text-gray-600"
          />
        </div>
      )}

      {/* Tabla */}
      <div className="rounded-xl border border-gray-200 bg-white overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between">
          <h3 className="font-bold text-gray-900">Productos con Menor Rotación</h3>
          <span className="text-sm text-gray-500">
            {productos.length} registros
          </span>
        </div>
        {isLoading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600" />
          </div>
        ) : productos.length === 0 ? (
          <div className="flex items-center justify-center h-64 text-gray-500">
            No hay datos de rotación para el período seleccionado
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-5 py-3 text-left text-xs font-medium text-gray-500 uppercase">Producto</th>
                  <th className="px-5 py-3 text-left text-xs font-medium text-gray-500 uppercase">Categoría</th>
                  <th className="px-5 py-3 text-right text-xs font-medium text-gray-500 uppercase">Cantidad Vendida</th>
                  <th className="px-5 py-3 text-right text-xs font-medium text-gray-500 uppercase">Total Ventas</th>
                  <th className="px-5 py-3 text-right text-xs font-medium text-gray-500 uppercase">Stock Base</th>
                  <th className="px-5 py-3 text-center text-xs font-medium text-gray-500 uppercase">Rotación</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {productos.map((p) => (
                  <tr key={p.id} className="hover:bg-gray-50">
                    <td className="px-5 py-3">
                      <p className="text-sm font-medium text-gray-900">{p.nombre}</p>
                    </td>
                    <td className="px-5 py-3 text-sm text-gray-500">{p.categoria}</td>
                    <td className="px-5 py-3 text-right text-sm font-medium text-gray-900">{p.cantidadVendida}</td>
                    <td className="px-5 py-3 text-right text-sm text-gray-500">{formatMoney(p.totalVentas)}</td>
                    <td className="px-5 py-3 text-right text-sm text-gray-500">{p.stockBase}</td>
                    <td className="px-5 py-3 text-center">
                      <span
                        className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${
                          p.cantidadVendida === 0
                            ? 'bg-red-100 text-red-700'
                            : p.cantidadVendida <= 5
                            ? 'bg-orange-100 text-orange-700'
                            : 'bg-yellow-100 text-yellow-700'
                        }`}
                      >
                        {p.cantidadVendida === 0 ? 'Sin ventas' : 'Baja'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};
